import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineFileDownload } from 'react-icons/md';

// Components
import Typography from '../../../../../components/Typography';
import Divider from '../../../../../components/divider';
import Button from '../../../../../components/CustomButton';

// Styles
import styles from './style.module.scss';

// Types
import { Podcast, PodcastSection } from '../../../../../types';
import AudioTracker from './Audio';
import AddToModal from '../../../../../components/AddTooModal';
import FeedbackModal from '../../../../../components/FeedbackModal';

interface SectionProps {
  section: PodcastSection;
}

const Section: React.FC<SectionProps> = ({ section }) => {
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  return (
    <div className={styles.section}>
      <Typography variant="p" className={styles.sectionTitle}>
        {`${formatTime(section.start)} - ${section.title}`}
      </Typography>

      <div className={styles.sectionActors}>
        {section.actors.map((actor) => (
          <div className={styles.sectionActor}>
            <div className={styles.sectionActorAvatarContainer}>
              <img src={actor.avatar} alt={actor.name} className={styles.sectionActorAvatar} />
            </div>

            <Typography variant="p" className={styles.sectionActorName}>
              {`${actor.name}:`}
            </Typography>

            <Typography variant="p" className={styles.sectionActorText}>
              {actor.text}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  )
}

interface PlayerProps {
  podcast: Podcast;
  onBack: () => void;
}

const Player: React.FC<PlayerProps> = ({ podcast, onBack }) => {
  const { t } = useTranslation();

  // States
  const [openAddToModal, setOpenAddToModal] = useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);

  // Handlers
  const handleDownload = useCallback(() => {
    // TODO: Connect to the backend
    // try {
    //   const response = await fetch(urls.downloadUrl(), {
    //     method: 'POST',
    //     headers: { 'CSRF-Token': csrfToken },
    //     body: data
    //   });
    // } catch (error) {
    //   console.error(error);
    // }
  }, []);

  const handleAddTo = useCallback(() => {
    setOpenAddToModal(true);
  }, []);

  const handleCloseAddToModal = useCallback(() => {
    setOpenAddToModal(false);
  }, []);

  const handleAdded = useCallback(() => {
    setOpenAddToModal(false);
    // TODO: Connect to the backend
  }, []);

  const handleLike = useCallback(() => {
    setOpenFeedbackModal(false);
    // TODO: Connect to the backend
  }, []);

  const handleDislike = useCallback(() => {
    setOpenFeedbackModal(false);
    // TODO: Connect to the backend
  }, []);

  const handleCloseFeedbackModal = useCallback(() => {
    setOpenFeedbackModal(false);
    // TODO: Connect to the backend
  }, []);


  return (
    <div className={styles.root}>
      <div className={styles.audioContainer}>
        <AudioTracker audioUrl={podcast.audio} sections={podcast.sections} />
      </div>

      <div className={styles.container}>
        <Typography variant="h2" className={styles.summaryTitle}>
          {t('generate.podcast.summary_label')}
        </Typography>

        <Typography variant="p" className={styles.summary}>
          {podcast.summary}
        </Typography>
      </div>

      <div className={styles.container}>
        <Typography variant="h2" className={styles.title}>
          {t('generate.podcast.transcript_label')}
        </Typography>

        <div className={styles.transcriptContainer}>
          {podcast.sections.map((section, index) => (
            <>
              <Section section={section} />

              {index !== podcast.sections.length - 1 && <Divider />}
            </>
          ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button variant="text" onClick={onBack} classes={{ root: styles.button_back }}>
          {t('generate.video.back')}
        </Button>

        <div className={styles.actions_right}>
          <Button
            variant="text"
            onClick={handleDownload}
            classes={{ root: styles.button_download }}
            icon={<MdOutlineFileDownload />}
          >
            {t('globals.download')}
          </Button>

          <Button variant="secondary" onClick={handleAddTo} classes={{ root: styles.button_add_to }}>
            {t('globals.add_to')}
          </Button>
        </div>
      </div>

      <AddToModal
        type='add'
        open={openAddToModal}
        onClose={handleCloseAddToModal}
        onAdded={handleAdded}
      />

      <FeedbackModal
        open={openFeedbackModal}
        onClose={handleCloseFeedbackModal}
        onLike={handleLike}
        onDislike={handleDislike}
        message={t('generate.video.feedback_message')}
      />
    </div >
  )
}

export default Player;
